// src/components/RateProvenance.tsx
//
// WHERE A SUBSIDY RATE CAME FROM — printed, never composed.
//
// == WHAT THIS COMPONENT IS ALLOWED TO DECIDE ==
//
// Only WHERE the sentence sits and what it looks like. Every claim in it is the
// server's: `services/gateway/src/rate-provenance.ts` publishes
// `app.ward_rule_version.note` verbatim, and `{provenance.note}` below is the
// whole of the text.
//
// A hard-coded "SYNTHETIC — fabricated" string here would be a second copy of a
// claim about the database, which is the class `published-bounds.test.ts` exists
// to census — and, specifically, a screen-side string cannot tell a fabricated
// CPW/FAW/CWM rate from one PSD authors in answer to Q-34. It would label both
// or neither: the first lies about the Authority's own policy, the second is the
// defect this component closes (2,838 of 5,117 demonstration bills showing
// invented subsidy money with nothing saying so).
//
// The one string this file does own is the HEADING, and it is navigational
// chrome rather than a claim: it tells a reader what the paragraph below is
// about and asserts nothing about the data. If the note is absent, the heading
// is absent too — the component renders nothing at all rather than a heading
// over emptiness, because a "Where this rate came from" label with no answer
// under it reads as a system that lost the answer.
//
// == WHY NOT AN SgdsAlert ==
//
// An alert is an interruption, and this is not an anomaly: on a demonstration
// estate it is the NORMAL state of 55% of bills. An alert on 55% of screens is
// the blanket banner a reader trains themselves to skip. This is a quiet, inset
// note bound to the figures it qualifies, in the same `.pt-well` surface
// `BillDetailPage`'s subsidy and co-payment boxes already use — no new
// foreground/background token pair, so `styles/token-cascade.test.ts`'s contrast
// census already covers it ('body copy on .pt-card / .pt-well').
//
// == PRINTING ==
//
// No `pt-no-print`. P2C1 §12.52 requires every query and report to be printable,
// and a printed bill whose provenance was dropped in the print stylesheet is
// exactly the artefact that would be handed to someone as evidence.
import type { ReactNode } from 'react'
import type { RateProvenance } from '../lib/types'

/**
 * The disclosure for ONE rate, or nothing.
 *
 * @param provenance what the server published for the applied rule — `null` when
 * nothing claims the rate is fabricated, which is the state of every MSO and CCS
 * bill and of any rate the Authority supplies.
 * @param heading owned by the caller so each surface can say what the figures
 * above it are (a computed split, an estimate, a table of settlement members)
 * without this component guessing.
 */
export function RateProvenanceNote({
  provenance,
  heading,
}: {
  provenance: RateProvenance | null | undefined
  heading: string
}): ReactNode {
  if (provenance === null || provenance === undefined) return null
  return (
    <div className="pt-well p-4 mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">{heading}</p>
      <p className="mt-2 text-sm text-[var(--sgds-body-color-default)]">{provenance.note}</p>
      <p className="mt-2 text-xs text-[var(--sgds-color-subtle)]">
        Applies to rule <span className="pt-mono">{provenance.ruleId}</span> v{provenance.version}.
      </p>
    </div>
  )
}

/**
 * The disclosure for a TABLE — one block per rule the rows actually rode.
 *
 * Renders nothing when the list is empty, which is what an MSO/CCS-only page
 * publishes. The rule id in each block is what ties a note to its rows: the
 * caller marks the affected rows with {@link rateProvenanceMarkedRows} and the
 * reader matches on the id they can see in the row's own Rule column: the caller
 * marks affected rows with {@link rateProvenanceKeys} and
 * {@link isRateProvenanceMarked}.
 */
export function RateProvenanceNotes({
  provenance,
  heading,
}: {
  provenance: readonly RateProvenance[]
  heading: string
}): ReactNode {
  if (provenance.length === 0) return null
  return (
    <div className="pt-well p-4 mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">{heading}</p>
      {provenance.map((entry) => (
        <p key={`${entry.ruleId} ${entry.version}`} className="mt-2 text-sm text-[var(--sgds-body-color-default)]">
          <span className="pt-mono">
            {entry.ruleId} v{entry.version}
          </span>:{' '}
          {entry.note}
        </p>
      ))}
    </div>
  )
}

/**
 * The set of `ruleId vN` keys a published disclosure covers, for marking rows.
 *
 * ⚠ Both halves of the key. `app.ward_rule_version`'s PRIMARY KEY is
 * `(id, version)`, so the day PSD supersedes the fabricated `CPW:B1 v1` with a
 * confirmed `v2`, rows computed under v1 must stay marked and rows under v2 must
 * not. A `Set` of bare ids would mark both, and it would be wrong about half of
 * them.
 */
export function rateProvenanceKeys(provenance: readonly RateProvenance[]): ReadonlySet<string> {
  return new Set(provenance.map((entry) => `${entry.ruleId} ${entry.version}`))
}

/** Is this row's applied rule one the server published a provenance note for? */
export function isRateProvenanceMarked(keys: ReadonlySet<string>, ruleId: string, version: number): boolean {
  return keys.has(`${ruleId} ${version}`)
}
